import { Injectable } from '@angular/core';
import {
  DEFAULT_TIMEZONE,
  DEFAULT_TIMEZONE_ABBREVIATION,
  DEFAULT_TIMEZONE_OFFSET,
} from '@core/constants';
import { TimezoneWrapper } from '@core/models/timezone-wrapper';
import { GeolocationService } from '@core/services/geolocation/geolocation.service';
import {
  SetShipmentDestination,
  SetShipmentOrigin,
} from '@core/state/ship-or-quote/ship-or-quote.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import deepEqual from 'deep-equal';
import {
  distinctUntilChanged,
  exhaustMap,
  firstValueFrom,
  map,
  tap,
} from 'rxjs';
import {
  SetSameDayDestination,
  SetSameDayOrigin,
} from '../same-day-courier/same-day-courier.actions';
import {
  SetSpecialtyColdChainDestination,
  SetSpecialtyColdChainOrigin,
} from '../specialty-cold-chain/specialty-cold-chain.actions';
import {
  LoadTimezonesProperties,
  LoadTimezonesProps,
  SetRecipientTimezone,
  SetSenderTimezone,
} from './timezones.actions';

@Injectable()
export class TimezonesEffects {
  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private geolocationService: GeolocationService
  ) {}

  loadTimezones$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoadTimezonesProps),
        exhaustMap(() => {
          return this.geolocationService.getTimeZones().pipe(
            map((res) => {
              const timezones: TimezoneWrapper[] = Array.isArray(res.data)
                ? res.data
                : [];

              this.store.dispatch(
                LoadTimezonesProperties({
                  timezonesProperties: timezones,
                })
              );
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  loadSenderTimezone = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SetShipmentOrigin,
          SetSameDayOrigin,
          SetSpecialtyColdChainOrigin
        ),
        map(({ value }) => {
          return {
            regionCode: value.shipmentAddress?.countryCode,
            state: value.shipmentAddress?.stateCode || '',
            zipCode: value.shipmentAddress?.zipCode || null,
            city: value.shipmentAddress?.city || '',
          };
        }),
        distinctUntilChanged((prev, curr) => {
          return deepEqual(prev, curr);
        }),
        tap(async (addressInfo) => {
          let tz = {
            tzIdentifier: DEFAULT_TIMEZONE,
            abbreviation: DEFAULT_TIMEZONE_ABBREVIATION,
            offset: DEFAULT_TIMEZONE_OFFSET,
          };
          if (addressInfo.city && addressInfo.regionCode) {
            this.geolocationService.validateTimezone(addressInfo).subscribe({
              next: async (res: any) => {
                let id = res.data?.id;
                if (res.success && res.data?.valid && id) {
                  // alternative way to get abbreviation
                  // let abbreviation = moment.tz(res.data?.timeZoneId).format('z'); // EDT or EST
                  let response: any = await firstValueFrom(
                    this.geolocationService.getTimezoneById(id)
                  );
                  tz = response.data;
                }
                this.store.dispatch(
                  SetSenderTimezone({
                    // senderTimezone: { abbreviation },
                    senderTimezone: tz,
                  })
                );
              },
            });
          }
        })
      ),
    { dispatch: false }
  );

  loadRecipientTimezone = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SetShipmentDestination,
          SetSameDayDestination,
          SetSpecialtyColdChainDestination
        ),
        map(({ value }) => {
          return {
            regionCode: value.shipmentAddress?.countryCode,
            state: value.shipmentAddress?.stateCode || '',
            zipCode: value.shipmentAddress?.zipCode || null,
            city: value.shipmentAddress?.city || '',
          };
        }),
        distinctUntilChanged((prev, curr) => {
          return deepEqual(prev, curr);
        }),
        tap(async (addressInfo) => {
          let tz = {
            tzIdentifier: DEFAULT_TIMEZONE,
            abbreviation: DEFAULT_TIMEZONE_ABBREVIATION,
            offset: DEFAULT_TIMEZONE_OFFSET,
          };
          if (addressInfo.city && addressInfo.regionCode) {
            this.geolocationService.validateTimezone(addressInfo).subscribe({
              next: async (res: any) => {
                let id = res.data?.id;
                if (res.success && res.data?.valid && id) {
                  // alternative way to get abbreviation
                  // let abbreviation = moment.tz(res.data?.timeZoneId).format('z'); // EDT or EST
                  let response: any = await firstValueFrom(
                    this.geolocationService.getTimezoneById(id)
                  );
                  tz = response.data;
                }
                this.store.dispatch(
                  SetRecipientTimezone({
                    // recipientTimezone: { abbreviation },
                    recipientTimezone: tz,
                  })
                );
              },
            });
          }
        })
      ),
    { dispatch: false }
  );
}
